import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';

export default function APCalculusBCPage() {
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: 'AP Calculus BC | Full Year College Calculus | Augustine Anyimadu-Ahenkae',
    description: 'Complete AP Calculus BC curriculum — all AB content plus parametric equations, polar coordinates, vector-valued functions, and infinite sequences/series. Equivalent to two semesters of college calculus.',
    url: `${siteUrl}/grades/ap-calculus-bc`,
    provider: { '@type': 'Person', name: 'Augustine Anyimadu-Ahenkae', url: siteUrl },
    educationalLevel: 'Advanced Placement',
    inLanguage: 'en',
    hasCourseInstance: [
      { '@type': 'CourseInstance', name: 'Units 1-8: All AP Calculus AB Content', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Unit 9: Parametric, Polar & Vector Functions', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Unit 10: Infinite Sequences and Series', courseMode: 'online' },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50">
      <SEOHead
        title="AP Calculus BC Curriculum | Parametric Polar Series | Augustine Anyimadu-Ahenkae"
        description="Complete AP Calculus BC curriculum — includes all AB content plus parametric equations, polar coordinates, vector-valued functions, and infinite series. Equivalent to two semesters of college calculus. AB subscore included."
        canonical="/grades/ap-calculus-bc"
        keywords="AP Calculus BC, parametric equations, polar coordinates, infinite series, Taylor Maclaurin series, vector functions, AP BC exam prep"
        jsonLd={jsonLd}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-700 via-indigo-700 to-blue-700 text-white">
        <div className="container mx-auto px-4 py-16">
          <Link 
            to="/grades/hub" 
            className="inline-flex items-center gap-2 text-purple-100 hover:text-white mb-6 transition-colors"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="text-lg">Back to Grades Hub</span>
          </Link>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <i className="ri-function-fill text-4xl"></i>
            </div>
            <div>
              <h1 className="text-5xl font-bold mb-2">AP Calculus BC</h1>
              <p className="text-purple-100 text-xl">Advanced calculus with parametric equations, polar coordinates & infinite series</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold">10</div>
              <div className="text-purple-100">Total Units</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold">8</div>
              <div className="text-purple-100">AB Units Included</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold">2</div>
              <div className="text-purple-100">BC-Only Units</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold">5</div>
              <div className="text-purple-100">AP Exam Score</div>
            </div>
          </div>
        </div>
      </div>

      {/* Course Overview */}
      <div className="container mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">AP Calculus BC Overview</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            AP Calculus BC is equivalent to two semesters of college calculus. It includes <strong>all AP Calculus AB topics</strong> 
            plus additional advanced material: parametric and polar equations, vector-valued functions, and infinite series. 
            BC students receive both a BC score and an AB subscore on the AP exam.
          </p>

          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-green-600"></i>
              What's Included in AP Calculus BC
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-purple-700 mb-3">All AB Content (Units 1-8):</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 mt-1"></i>
                    <span>Limits and Continuity</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 mt-1"></i>
                    <span>Differentiation (Definition, Rules, Applications)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 mt-1"></i>
                    <span>Integration and Accumulation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 mt-1"></i>
                    <span>Differential Equations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 mt-1"></i>
                    <span>Applications of Integration</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-indigo-700 mb-3">BC-Only Content (Units 9-10):</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-star-fill text-indigo-600 mt-1"></i>
                    <span><strong>Parametric Equations</strong> - Motion in the plane</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-star-fill text-indigo-600 mt-1"></i>
                    <span><strong>Polar Coordinates</strong> - Curves in polar form</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-star-fill text-indigo-600 mt-1"></i>
                    <span><strong>Vector-Valued Functions</strong> - Position, velocity, acceleration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-star-fill text-indigo-600 mt-1"></i>
                    <span><strong>Infinite Sequences</strong> - Convergence and divergence</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-star-fill text-indigo-600 mt-1"></i>
                    <span><strong>Infinite Series</strong> - Taylor/Maclaurin series, power series</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-star-fill text-indigo-600 mt-1"></i>
                    <span><strong>Convergence Tests</strong> - Ratio, root, comparison, alternating series</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-amber-600 text-2xl mt-1"></i>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Important Note</h3>
                <p className="text-gray-700 leading-relaxed">
                  AP Calculus BC covers <strong>all content from AP Calculus AB</strong> plus additional topics. 
                  If you're taking BC, you should first master all AB material (Units 1-8) before moving to BC-specific topics (Units 9-10). 
                  Start with the AP Calculus AB lessons below!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* AB Content */}
          <Link 
            to="/grades/ap-calculus-ab"
            className="bg-white rounded-xl shadow-md p-8 border-2 border-transparent hover:border-blue-500 hover:shadow-xl transition-all group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                <i className="ri-function-line text-3xl text-blue-600 group-hover:text-white"></i>
              </div>
              <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-blue-600 transition-colors"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">AP Calculus AB Lessons</h3>
            <p className="text-gray-600 mb-4">
              Master Units 1-8: Limits, Derivatives, Integrals, and Differential Equations
            </p>
            <div className="flex items-center gap-2 text-blue-600 font-semibold">
              <span>Start with AB content</span>
              <i className="ri-arrow-right-line"></i>
            </div>
          </Link>

          {/* BC-Only Content */}
          <div className="bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-xl shadow-md p-8">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm mb-4">
              <i className="ri-star-fill text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-2">BC-Only Topics</h3>
            <p className="text-purple-100 mb-4">
              Advanced topics: Parametric, Polar, Vectors & Series
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line"></i>
                <span>Unit 9: Parametric, Polar & Vector Functions</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line"></i>
                <span>Unit 10: Infinite Sequences and Series</span>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/20">
              <div className="text-sm text-purple-100">Coming soon - full BC-only lessons</div>
            </div>
          </div>
        </div>

        {/* Unit Breakdown */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Complete Unit Breakdown</h2>
          
          <div className="space-y-4">
            {/* AB Units */}
            <div className="border-l-4 border-blue-600 pl-6 py-2">
              <h3 className="text-xl font-bold text-gray-900">Units 1-8: AP Calculus AB Content</h3>
              <p className="text-gray-600 mb-3">Available in the AP Calculus AB section</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="text-gray-700">
                  <strong>Unit 1:</strong> Limits and Continuity
                </div>
                <div className="text-gray-700">
                  <strong>Unit 2:</strong> Differentiation - Definition & Properties
                </div>
                <div className="text-gray-700">
                  <strong>Unit 3:</strong> Composite, Implicit & Inverse Functions
                </div>
                <div className="text-gray-700">
                  <strong>Unit 4:</strong> Contextual Applications of Differentiation
                </div>
                <div className="text-gray-700">
                  <strong>Unit 5:</strong> Analytical Applications of Differentiation
                </div>
                <div className="text-gray-700">
                  <strong>Unit 6:</strong> Integration and Accumulation
                </div>
                <div className="text-gray-700">
                  <strong>Unit 7:</strong> Differential Equations and Applications
                </div>
                <div className="text-gray-700">
                  <strong>Unit 8:</strong> Applications of Integration
                </div>
              </div>
            </div>

            {/* BC-Only Units */}
            <div className="border-l-4 border-purple-600 pl-6 py-2 bg-purple-50">
              <h3 className="text-xl font-bold text-gray-900">Units 9-10: BC-Only Content</h3>
              <p className="text-gray-600 mb-3">Advanced topics beyond AP Calculus AB</p>
              <div className="space-y-3">
                <div>
                  <div className="font-bold text-purple-700 mb-1">Unit 9: Parametric Equations, Polar Coordinates & Vector-Valued Functions</div>
                  <ul className="ml-4 space-y-1 text-gray-700 text-sm">
                    <li>• Parametric equations for motion in the plane</li>
                    <li>• Derivatives and integrals of parametric curves</li>
                    <li>• Polar coordinate system and polar curves</li>
                    <li>• Area and arc length in polar coordinates</li>
                    <li>• Vector-valued functions for position, velocity, and acceleration</li>
                  </ul>
                </div>
                <div>
                  <div className="font-bold text-purple-700 mb-1">Unit 10: Infinite Sequences and Series</div>
                  <ul className="ml-4 space-y-1 text-gray-700 text-sm">
                    <li>• Convergence and divergence of sequences</li>
                    <li>• Geometric series and telescoping series</li>
                    <li>• Convergence tests: Integral, Comparison, Limit Comparison, Ratio, Root</li>
                    <li>• Alternating series and absolute vs. conditional convergence</li>
                    <li>• Power series and radius/interval of convergence</li>
                    <li>• Taylor and Maclaurin series</li>
                    <li>• Applications of series approximations</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AP Exam Information */}
        <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl shadow-lg p-8 text-white">
          <h2 className="text-3xl font-bold mb-4">AP Calculus BC Exam</h2>
          <p className="text-purple-100 text-lg mb-6">
            The AP Calculus BC exam consists of 45 multiple-choice questions and 6 free-response questions. 
            You'll receive both a BC score (1-5) and an AB subscore based on AB content only.
          </p>
          
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">3 hours 15 min</div>
              <div className="text-purple-100 text-sm">Total Exam Time</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">50% / 50%</div>
              <div className="text-purple-100 text-sm">MCQ / FRQ Weight</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-2xl font-bold mb-1">~40%</div>
              <div className="text-purple-100 text-sm">BC-Only Content</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to="/grades/ap-calculus-bc-practice-exam"
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg p-4 transition-all hover:scale-105"
            >
              <i className="ri-file-list-3-line text-3xl mb-2"></i>
              <div className="font-semibold">BC Practice Exam</div>
              <div className="text-sm text-purple-100">Full-length practice test</div>
            </Link>
            <Link
              to="/grades/ap-calculus-ab"
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg p-4 transition-all hover:scale-105"
            >
              <i className="ri-book-open-line text-3xl mb-2"></i>
              <div className="font-semibold">Study AB Content First</div>
              <div className="text-sm text-purple-100">Master Units 1-8</div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}